import type { ArrivalTypeRecord } from "@/types/camping";
import {
  NUMERIC_CONSTRAINT_FIELDS,
  type ArrivalTypePitchSettings,
  type PitchArrivalTypeSettingsForm,
} from "@/lib/pitchUsageType";

type Props = {
  arrivalTypes: ArrivalTypeRecord[];
  restrictArrivalTypes: boolean;
  onRestrictChange: (value: boolean) => void;
  value: PitchArrivalTypeSettingsForm;
  onChange: (value: PitchArrivalTypeSettingsForm) => void;
  disabled?: boolean;
};

export function PitchArrivalTypeSettingsEditor({
  arrivalTypes,
  restrictArrivalTypes,
  onRestrictChange,
  value,
  onChange,
  disabled,
}: Props) {
  const sortedTypes = [...arrivalTypes].sort((a, b) => a.sort_order - b.sort_order);

  function updateType(code: string, patch: Partial<ArrivalTypePitchSettings>) {
    const current = value[code] ?? { allowed: false, constraints: {} };
    onChange({
      ...value,
      [code]: { ...current, ...patch },
    });
  }

  function updateConstraint(
    code: string,
    fieldKey: (typeof NUMERIC_CONSTRAINT_FIELDS)[number]["key"],
    bound: "min" | "max",
    next: string,
  ) {
    const current = value[code] ?? { allowed: false, constraints: {} };
    onChange({
      ...value,
      [code]: {
        ...current,
        constraints: {
          ...current.constraints,
          [fieldKey]: {
            min: bound === "min" ? next : (current.constraints[fieldKey]?.min ?? ""),
            max: bound === "max" ? next : (current.constraints[fieldKey]?.max ?? ""),
          },
        },
      },
    });
  }

  return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-sm text-label">
        <input
          type="checkbox"
          className="rounded border-separator text-accent focus:ring-accent"
          checked={restrictArrivalTypes}
          onChange={(e) => onRestrictChange(e.target.checked)}
          disabled={disabled}
        />
        Anreisearten einschränken
      </label>
      <p className="text-xs text-label-tertiary">
        Ohne Einschränkung sind alle Anreisearten erlaubt. Mit Einschränkung können Sie pro
        Anreiseart Min-/Max-Werte für Fahrzeugmaße definieren.
      </p>
      {restrictArrivalTypes ? (
        <div className="space-y-3">
          {sortedTypes.map((type) => {
            const entry = value[type.code] ?? { allowed: false, constraints: {} };
            return (
              <div key={type.code} className="rounded-lg border border-separator p-3">
                <label className="flex items-center gap-2 text-sm font-medium text-label">
                  <input
                    type="checkbox"
                    className="rounded border-separator text-accent focus:ring-accent"
                    checked={entry.allowed}
                    onChange={(e) => updateType(type.code, { allowed: e.target.checked })}
                    disabled={disabled}
                  />
                  {type.name}
                </label>
                {entry.allowed ? (
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full min-w-[32rem] text-left text-sm">
                      <thead className="border-b border-separator text-label-secondary">
                        <tr>
                          <th className="px-2 py-1 font-medium">Eigenschaft</th>
                          <th className="px-2 py-1 font-medium">Min.</th>
                          <th className="px-2 py-1 font-medium">Max.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {NUMERIC_CONSTRAINT_FIELDS.map((field) => {
                          const bounds = entry.constraints[field.key] ?? { min: "", max: "" };
                          return (
                            <tr key={field.key} className="border-b border-separator last:border-0">
                              <td className="px-2 py-2 text-label">{field.label}</td>
                              <td className="px-2 py-2">
                                <input
                                  className="eh-input w-full max-w-[8rem]"
                                  value={bounds.min}
                                  onChange={(e) =>
                                    updateConstraint(type.code, field.key, "min", e.target.value)
                                  }
                                  inputMode="decimal"
                                  placeholder="—"
                                  disabled={disabled}
                                />
                              </td>
                              <td className="px-2 py-2">
                                <input
                                  className="eh-input w-full max-w-[8rem]"
                                  value={bounds.max}
                                  onChange={(e) =>
                                    updateConstraint(type.code, field.key, "max", e.target.value)
                                  }
                                  inputMode="decimal"
                                  placeholder="—"
                                  disabled={disabled}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
